import { useState, useCallback } from 'react';
import useReconciliationMutation from './useReconciliationMutation';

export default function useReconciliationMismatchDialog({ type, onSuccess }) {
    const [showMismatchDialog, setShowMismatchDialog] = useState(false);
    const [pendingReconciliation, setPendingReconciliation] = useState(null);

    const [showForceAssociatedDialog, setShowForceAssociatedDialog] = useState(false);
    const [pendingForceAssociated, setPendingForceAssociated] = useState(null);

    const {
        markInvoiceAsReconciled,
        saveInvoiceAssociations,
        markTransactionAsReconciled,
        saveTransactionAssociations,
    } = useReconciliationMutation();

    const openMismatchDialog = useCallback((entityId, associatedIds, pendingAmount) => {
        setPendingReconciliation({ entityId, associatedIds, pendingAmount });
        setShowMismatchDialog(true);
    }, []);

    const closeMismatchDialog = useCallback(() => {
        setShowMismatchDialog(false);
        setPendingReconciliation(null);
    }, []);

    const handleMismatchReconcile = useCallback(
        (reconcileOptions) => {
            if (!pendingReconciliation) return;

            const { entityId, associatedIds } = pendingReconciliation;

            if (type === 'invoice') {
                markInvoiceAsReconciled(entityId, associatedIds, reconcileOptions);
            } else {
                markTransactionAsReconciled(entityId, associatedIds, reconcileOptions);
            }

            closeMismatchDialog();
            onSuccess?.();
        },
        [
            pendingReconciliation,
            type,
            markInvoiceAsReconciled,
            markTransactionAsReconciled,
            closeMismatchDialog,
            onSuccess,
        ],
    );

    const handleMismatchSave = useCallback(() => {
        if (!pendingReconciliation) return;

        const { entityId, associatedIds } = pendingReconciliation;

        if (type === 'invoice') {
            saveInvoiceAssociations(entityId, associatedIds);
        } else {
            saveTransactionAssociations(entityId, associatedIds);
        }

        closeMismatchDialog();
        onSuccess?.();
    }, [
        pendingReconciliation,
        type,
        saveInvoiceAssociations,
        saveTransactionAssociations,
        closeMismatchDialog,
        onSuccess,
    ]);

    const checkAndReconcile = useCallback(
        (entityId, associatedIds, pendingAmount) => {
            if (pendingAmount !== 0) {
                openMismatchDialog(entityId, associatedIds, pendingAmount);
                return;
            }

            if (type === 'invoice') {
                markInvoiceAsReconciled(entityId, associatedIds);
            } else {
                markTransactionAsReconciled(entityId, associatedIds);
            }

            onSuccess?.();
        },
        [type, markInvoiceAsReconciled, markTransactionAsReconciled, openMismatchDialog, onSuccess],
    );

    const forceReconcile = useCallback(
        (entityId, associatedIds = []) => {
            if (type === 'invoice') {
                markInvoiceAsReconciled(entityId, associatedIds);
            } else {
                markTransactionAsReconciled(entityId, associatedIds);
            }

            onSuccess?.();
        },
        [type, markInvoiceAsReconciled, markTransactionAsReconciled, onSuccess],
    );

    const openForceAssociatedDialog = useCallback((entityId, associatedIds) => {
        setPendingForceAssociated({ entityId, associatedIds });
        setShowForceAssociatedDialog(true);
    }, []);

    const closeForceAssociatedDialog = useCallback(() => {
        setShowForceAssociatedDialog(false);
        setPendingForceAssociated(null);
    }, []);

    const handleForceAssociatedConfirm = useCallback(() => {
        if (!pendingForceAssociated) return;
        const { entityId, associatedIds } = pendingForceAssociated;
        if (type === 'invoice') {
            markInvoiceAsReconciled(entityId, associatedIds, { forceTransactionsReconciliationStatus: true });
        } else {
            markTransactionAsReconciled(entityId, associatedIds, { forceInvoicesReconciliationStatus: true });
        }
        closeForceAssociatedDialog();
        onSuccess?.();
    }, [pendingForceAssociated, type, markInvoiceAsReconciled, markTransactionAsReconciled, closeForceAssociatedDialog, onSuccess]);

    const handleForceAssociatedDecline = useCallback(() => {
        if (!pendingForceAssociated) return;
        const { entityId, associatedIds } = pendingForceAssociated;
        if (type === 'invoice') {
            markInvoiceAsReconciled(entityId, associatedIds);
        } else {
            markTransactionAsReconciled(entityId, associatedIds);
        }
        closeForceAssociatedDialog();
        onSuccess?.();
    }, [pendingForceAssociated, type, markInvoiceAsReconciled, markTransactionAsReconciled, closeForceAssociatedDialog, onSuccess]);

    return {
        showMismatchDialog,
        closeMismatchDialog,
        handleMismatchReconcile,
        handleMismatchSave,
        checkAndReconcile,
        forceReconcile,
        showForceAssociatedDialog,
        openForceAssociatedDialog,
        closeForceAssociatedDialog,
        handleForceAssociatedConfirm,
        handleForceAssociatedDecline,
        itemType: type === 'invoice' ? 'transaction' : 'invoice',
        mismatchAmount: pendingReconciliation?.pendingAmount ?? 0,
    };
}
